import React, { useEffect, useState } from "react";
import { valueToString, displayPercent } from "helpers/Helpers";
import { useParams, useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { showError, showSuccess } from "redux/actions/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName, faPlus } from "@fortawesome/pro-solid-svg-icons";
import {
  faWallet,
  faVault,
  faSackDollar,
  faCheckCircle,
  faCircleInfo,
  faTurnDownRight,
  faMoneyBill,
  faPenToSquare,
  faBan,
  faPen,
  faClock,
  faCoins,
} from "@fortawesome/pro-regular-svg-icons";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";
import axios, { CancelToken } from "axios";

import {
  GetInvoice,
  GetInvoiceLineItem,
  GetDownloadInvoice,
  PutVoidInvoice,
  PostDuplicateInvoice,
  PutApplyDeposit,
  PutDeposit,
  GetInvoiceTransaction,
  PostInvoiceTransactionRefund,
  PostBalanceAdjustment,
  PostEmailInvoice,
} from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { GetGiftCard, PrintGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { Badge } from "components/badge/Badge";
import InvoicePaymentModal from "./InvoicePaymentModal";
import { IInvoice, IInvoiceLineItem, IInvoiceTransaction } from "redux/reducers/models/order";
import { loadPaymentOptions } from "redux/actions/facility";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { ButtonNew as Button } from "components/buttonNew";

import "pages/secure/facility/order/order.scss";
import Input from "components/form/input";
import { useTranslation } from "react-i18next";
import EmailInvoiceModal, { IEMail } from "./modals/EmailModal";
import DataTable from "components/dataTable/DataTable";
import Sheet from "components/sheet/Sheet";
import { IGiftCard } from "api/rpc/2022-09/facilityAdmin/payment/giftCard";
import classNames from "classnames";
import InvoicePageSkeleton from "./InvoicePageSkeleton";
import Divider from "components/divider";
import Markdown from "components/markdown/Markdown";

import "./invoice.scss";
import { depositAmountToShow, visibleInvoiceDeposits } from "./invoiceDeposits";

interface IGiftCardInterface {
  giftCards: IGiftCard[],
}
export default function Invoice() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const terminalStore = useAppSelector(store => store.terminalStore);
  const { t } = useTranslation();
  const { StripeTerminalRefundSheet, processRefund } = useStripeTerminalPayment();

  const [invoice, setInvoice] = useState<IInvoice>(undefined);
  const [invoiceLineItems, setInvoiceLineItems] = useState<Array<IInvoiceLineItem>>(undefined);
  const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);
  const [depositModalVisible, setDepositModalVisible] = useState<boolean>(false);
  const [events, setEvents] = useState<Array<any>>(undefined);
  const [eventQuery, setEventQuery] = useState("");

  const {
    state: voidInvoicePopup,
    closeModal: closeVoidInvoicePopup,
    updateModal: updateVoidInvoicePopup,
  } = useModal();

  const {
    state: duplicateInvoicePopup,
    closeModal: closeDuplicateInvoicePopup,
    updateModal: updateDuplicateInvoicePopup,
  } = useModal();

  const {
    state: addAdjustmentsPopup,
    closeModal: closeAddAdjustmentsPopup,
    updateModal: updateAddAdjustmentsPopup,
  } = useModal();

  const {
    state: applyDepositPopup,
    updateModal: updateApplyDepositPopup,
    closeModal: closeApplyDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: removeDepositPopup,
    updateModal: updateRemoveDepositPopup,
    closeModal: closeRemoveDepositPopup,
  } = useModal({ depositId: null });

  const {
    state: adjustBalancePopup,
    updateModal: updateAdjustBalancePopup,
    closeModal: closeAdjustBalancePopup,
  } = useModal({
    newBalance: 0,
  });

  const {
    state: giftCardSheet,
    updateModal: updateGiftCardSheet,
    closeModal: closeGiftCardSheet,
  } = useModal();

  const {
    state: sendInvoiceSheet,
    updateModal: updateSendInvoiceSheet,
    closeModal: closeSendInvoiceSheet,
  } = useModal<IEMail>({
    newEmail: "",
    emails: [],
    invoiceID: null,
  });

  const [giftCardState, setGiftCardState] = useState<IGiftCardInterface>({
    giftCards: [],
  });

  // Track locally refunded transactions (for "Refunded" label before reload)
  const [locallyRefundedIds, setLocallyRefundedIds] = useState<Set<number>>(new Set());

  const { facilityStore } = useAppSelector(store => store);

  useEffect(() => {
    void dispatch(loadPaymentOptions());
  }, []);

  useEffect(() => {
    void loadInvoice();
  }, [id]);

  useEffect(() => {
    if (!invoice || (!invoice?.tournament_id && !invoice?.hospitality_event_id)) {
      const source = axios.CancelToken.source();
      void loadEvents(source.token);
      return () => {
        source.cancel();
      };
    }
  }, [eventQuery, invoice?.id, invoice?.tournament_id, invoice?.hospitality_event_id]);

  async function loadEvents(token: CancelToken) {
    let tournaments: Array<any> = [];
    let hospitalityEvents: Array<any> = [];
    setEvents(null);

    const tournamentRes = await GetTournament({ search: eventQuery }, false, token);
    if (token?.reason) {
      return;
    }
    if (tournamentRes.status === StatusCode.OK) {
      tournaments = tournamentRes.data.map(tournament => ({ ...tournament, event_type: "tournament" }));
    }

    const eventRes = await GetEvent({ search: eventQuery }, false, token);
    if (token?.reason) {
      return;
    }
    if (eventRes.status === StatusCode.OK) {
      hospitalityEvents = eventRes.data.map(event => ({ ...event, event_type: "hospitality" }));
    }

    setEvents(tournaments.concat(hospitalityEvents));
  }

  async function loadInvoice() {
    const invoiceRes = await GetInvoice(
      {
        id: Number(id),
        extended: true,
      },
      false,
    );

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.001")));
      return;
    }

    invoiceRes.data[0].transactions = determineRefundedTransactions(invoiceRes.data[0]?.transactions);

    if (invoiceRes.data[0].tournament_id) {
      const tournamentRes = await GetTournament({ id: invoiceRes.data[0].tournament_id }, false);

      if (tournamentRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.003")));
        return;
      }

      invoiceRes.data[0].tournament = tournamentRes.data[0];

      const depositRes = await GetInvoiceTransaction({ tournament_id: tournamentRes.data[0].id }, false);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.004")));
        return;
      }

      invoiceRes.data[0].tournament.deposits = depositRes.data;
    } else if (invoiceRes.data[0].hospitality_event_id) {
      const eventRes = await GetEvent({ id: invoiceRes.data[0].hospitality_event_id }, false);

      if (eventRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.005")));
        return;
      }

      invoiceRes.data[0].hospitality_event = eventRes.data[0];

      const depositRes = await GetInvoiceTransaction({ hospitality_event_id: eventRes.data[0].id }, false);

      if (depositRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice.006")));
        return;
      }

      invoiceRes.data[0].hospitality_event.deposits = depositRes.data;
    }

    const lineItemRes = await GetInvoiceLineItem({ invoice_id: invoiceRes.data[0].id }, false);

    if (lineItemRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.007")));
      return;
    }

    const filteredGiftCards = lineItemRes.data.filter(lineItem => lineItem.product.type === 'Gift Card');
    if (filteredGiftCards.length > 0) {
      void loadGiftCards(invoiceRes.data[0].id);
    }


    setInvoice(invoiceRes.data[0]);
    setInvoiceLineItems(lineItemRes.data);
  }

  async function loadGiftCards(invoice_id: number) {
    const giftCardRes = await GetGiftCard({ invoice_id: invoice_id }, true);
    if (giftCardRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.order.071")));
    }

    setGiftCardState({ giftCards: giftCardRes.data });
  }

  async function downloadInvoice() {
    const invoiceRes = await GetDownloadInvoice({ invoice_id: Number(id) }, true);

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.008")));
      return;
    }

    window.open(String(invoiceRes.data));
  }

  async function voidInvoice() {
    const voidRes = await PutVoidInvoice({ invoice_id: Number(id) }, true);

    if (voidRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.009")));
      return;
    }

    void loadInvoice();
    updateVoidInvoicePopup({ isOpen: false });
  }

  async function duplicateInvoice() {
    const duplicateRes = await PostDuplicateInvoice({ invoice_id: Number(id) }, true);

    if (duplicateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.010")));
      return;
    }

    updateDuplicateInvoicePopup({ isOpen: false });
    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.011")));
  }

  async function editInvoice() {
    const duplicateRes = await PostDuplicateInvoice(
      {
        invoice_id: Number(id),
        adjustment: true,
      },
      true,
    );

    if (duplicateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.012")));
      return;
    }

    closeAddAdjustmentsPopup();
    dispatch(showSuccess(t("Draft invoice created successfully")));
    history.push(`/admin/invoices/${String(duplicateRes.data.id)}/edit`);
  }

  async function applyDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.014")));
      return;
    }

    if (invoice.financial_status !== "open" && invoice.financial_status !== "draft") {
      dispatch(showError(t("secure.facility.order.invoices.invoice.015")));
      return;
    }

    const applyRes = await PutApplyDeposit(
      {
        invoice_id: Number(id),
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.016")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.017")));
    updateApplyDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  async function removeDeposit(depositId: number) {
    if (!depositId) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.018")));
      return;
    }

    const applyRes = await PutDeposit(
      {
        invoice_id: null,
        deposit_id: depositId,
      },
      true,
    );

    if (applyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.019")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice.020")));
    updateRemoveDepositPopup({ isOpen: false, depositId: null });
    void loadInvoice();
  }

  function getBalance(): number {
    if (!invoice?.transactions) {
      return;
    }

    let total = invoice?.total_price;

    for (let i = 0; i < invoice?.transactions.length; i++) {
      if (invoice?.transactions[i].kind === "refund" || invoice?.transactions[i].kind === "invoice_debit_adjustment") {
        total += invoice?.transactions[i].amount;
      } else {
        total -= invoice?.transactions[i].amount;
      }
    }

    return total;
  }

  function getDepositsApplied(): number {
    if (!invoice?.transactions) {
      return 0;
    }

    const depositIds = new Set(
      invoice.transactions.filter(transaction => transaction.kind === "deposit").map(transaction => transaction.id),
    );

    let sum = 0;
    for (const transaction of invoice.transactions) {
      if (transaction.kind === "deposit") {
        sum += Number(transaction.amount) || 0;
      } else if (transaction.kind === "refund" && depositIds.has(Number(transaction.parent_id))) {
        sum -= Number(transaction.amount) || 0;
      }
    }
    return sum;
  }

  /** Refunded transaction based off parent_id of the transaction == the currentTransactionId */
  function determineRefundedTransactions(transactions: IInvoiceTransaction[]): IInvoiceTransaction[] {
    const filteredTransactions = transactions.filter(
      (transaction: Record<string, any>) => transaction?.kind !== "deposit_authorization",
    );
    const refundedTransactions = transactions.filter(
      (transaction: Record<string, any>) => transaction?.parent_id && transaction?.kind === "refund",
    );

    const markRefunds = [...filteredTransactions]?.reduce(
      (markedRefunds, currentTransaction, index) => {
        const refunded = refundedTransactions?.some(
          (transaction: Record<string, any>) => transaction?.parent_id === currentTransaction?.id,
        );

        // Adding in 'refunded' param to HouseAccountTransactionType
        if (refunded) {
          markedRefunds[index] = { ...markedRefunds[index], refunded: true };
        } else {
          markedRefunds[index] = { ...markedRefunds[index], refunded: false };
        }
        return markedRefunds;
      },
      [...filteredTransactions],
    );

    return markRefunds;
  }

  async function postRefund(transaction: IInvoiceTransaction) {
    //Handle interac refunds
    if (transaction?.payment_type === "interac") {
      if (terminalStore?.reader) {
        const refundRes = await processRefund(transaction?.amount, transaction?.payment_intent_id);
        if (refundRes === "success") {
          dispatch(showSuccess(t("secure.facility.order.invoices.invoice.021")));
        } else {
          return;
        }
      } else {
        dispatch(showSuccess(t("secure.facility.order.invoices.invoice.023")));
        return;
      }
    }

    const refundRes = await PostInvoiceTransactionRefund({ transaction_id: transaction?.id }, true);

    if (refundRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice.024")));
      return;
    }

    setLocallyRefundedIds(prev => new Set(prev).add(transaction.id));
    void loadInvoice();
  }

  async function sendInvoice() {
    const params = {
      invoice_id: Number(id),
      emails: sendInvoiceSheet.emails,
    };

    const res = await PostEmailInvoice(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred while sending email")));
      return;
    }

    dispatch(showSuccess(t("Invoices successfully sent")));
    closeSendInvoiceSheet();
  }

  async function handleGiftCardPrint(lineItemId: number, e: React.MouseEvent) {
    e.stopPropagation();
    const params = {
      id: lineItemId,
    };

    const downloadReceiptRes = await PrintGiftCard(params, true);

    if (downloadReceiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.026")));

      return;
    }

    window.open().document.write(downloadReceiptRes.data as string);

    return;
  }

  async function createBalanceAdjustment() {
    const adjustmentRes = await PostBalanceAdjustment(
      {
        invoice_id: invoice?.id,
        new_balance: adjustBalancePopup.newBalance,
      },
      true,
    );

    if (adjustmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating balance adjustment")));
      return;
    }

    void loadInvoice();
    closeAdjustBalancePopup();
  }

  function navigateToParentInvoice() {
    if (!invoice?.parent_id) {
      return;
    }

    history.push(`/admin/invoices/${invoice.parent_id}`);
  }

  function navigateToGiftCard(giftCardCode: string) {
    history.push("/admin/giftcards/" + giftCardCode);
  }

  function formatCurrency(amount: number) {
    return Intl.NumberFormat("en-CA", { style: "currency", currency: invoice?.currency || "CAD" }).format(amount);
  }

  function getPaymentTermsLabel(invoiceDue?: string | null) {
    switch (invoiceDue) {
      case "on_receipt":
        return t("On Receipt");
      case "net_30":
        return t("Net 30");
      case "net_60":
        return t("Net 60");
      default:
        return null;
    }
  }

  const depositsApplied = getDepositsApplied();
  const balance = getBalance();
  const hasEvent = !!(invoice?.tournament || invoice?.hospitality_event);
  // Event deposits are loaded for the invoice's tournament/hospitality event.
  // Show ones applied to this invoice and ones still unapplied (invoice_id null),
  // including a deposit that was only partially refunded.
  // Deposits already applied to a different invoice stay hidden.
  const eventDeposits = invoice?.tournament?.deposits || invoice?.hospitality_event?.deposits || [];
  const visibleDeposits = visibleInvoiceDeposits(eventDeposits, invoice?.id);
  const visiblePayments =
    invoice?.transactions?.filter(
      transaction =>
        transaction.kind !== "deposit" &&
        transaction.kind !== "deposit_authorization" &&
        transaction.kind !== "refund",
    ) ?? [];
  const hasNoPaymentActivity = visibleDeposits.length === 0 && visiblePayments.length === 0;
  const financialStatus =
    (invoice?.voided_at || invoice?.financial_status === "voided")
      ? "voided"
      : invoice?.financial_status;
  const isVoided = financialStatus === "voided";
  const paymentTermsLabel = getPaymentTermsLabel(invoice?.invoice_due);
  const registerTitle =
    invoice?.register?.title ||
    facilityStore?.registers?.find(register => register.id === invoice?.register_id)?.title ||
    null;
  if (invoice === undefined || invoiceLineItems === undefined) {
    return (
      <Page
        title={t("secure.facility.order.invoices.invoice.025")}
        breadcrumbs={[
          { prefix: true, label: t("secure.facility.order.invoices.invoice.026"), url: "/admin/invoices" },
        ]}
        splitLayout
      >
        <InvoicePageSkeleton
          itemsTitle={t("secure.facility.order.invoices.invoice.032")}
          sidebarTitle={t("secure.facility.order.invoices.invoice.041")}
        />
      </Page>
    );
  }

  return (
    <Page
      title={`${t("secure.facility.order.invoices.invoice.025")} ${invoice?.name}`}
      titleMetadata={generateStatusBadge(financialStatus)}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.order.invoices.invoice.026"), url: "/admin/invoices" }]}
      splitLayout
      className="invoice-details-page"
      textAction={{
        content: t("Edit invoice"),
        icon: faPenToSquare,
        action: () => updateAddAdjustmentsPopup({ isOpen: true }),
        hidden: isVoided || (financialStatus !== "open" && financialStatus !== "partially_paid"),
      }}
      belowHeaderContent={
        <Card className="invoice-summary-card">
          <div className="invoice-summary-stats">
            <div className="invoice-summary-stat">
              <div className="invoice-summary-stat__icon">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="invoice-summary-stat__content">
                <p className="invoice-summary-stat__value">{formatCurrency(invoice?.total_price)}</p>
                <p className="invoice-summary-stat__label">{t("Total")}</p>
              </div>
            </div>
            <div className="invoice-summary-stat__divider" />
            <div className="invoice-summary-stat">
              <div className="invoice-summary-stat__icon">
                <FontAwesomeIcon icon={faVault} />
              </div>
              <div className="invoice-summary-stat__content">
                <p className="invoice-summary-stat__value">
                  {depositsApplied > 0 ? `-${formatCurrency(depositsApplied)}` : formatCurrency(0)}
                </p>
                <p className="invoice-summary-stat__label">{t("Deposits applied")}</p>
              </div>
            </div>
            <div className="invoice-summary-stat__divider" />
            <div className="invoice-summary-stat">
              <div className="invoice-summary-stat__icon">
                <FontAwesomeIcon icon={faSackDollar} />
              </div>
              <div className="invoice-summary-stat__content">
                <p className="invoice-summary-stat__value">{formatCurrency(balance)}</p>
                <p className="invoice-summary-stat__label">
                  {t("Balance due")}
                  {paymentTermsLabel ? ` • ${paymentTermsLabel}` : ""}
                  {invoice?.due_date ? ` • ${t("Due on")} ${invoice.due_date}` : ""}
                </p>
              </div>
            </div>
          </div>
        </Card>
      }
      multipleActionDropdownAction={{
        label: t("secure.facility.order.invoices.invoice.027"),
        dropdownProps: {
          alignment: "right",
          options: isVoided
            ? [
                {
                  type: "handler",
                  label: t("secure.facility.order.invoices.invoice.029"),
                  icon: "download",
                  handler: () => void downloadInvoice(),
                },
                {
                  type: "handler",
                  label: t("Send To"),
                  icon: "mail-forward",
                  handler: () => updateSendInvoiceSheet({ isOpen: true, emails: [invoice?.customer?.email] }),
                },
              ]
            : [
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.028"),
              icon: "money-bill",
              handler: () => setPaymentModalVisible(true),
              disabled: financialStatus === "paid",
            },
              {
                type: "handler",
                label: t("Make Deposit"),
                icon: "money-check-dollar",
                handler: () => setDepositModalVisible(true),
              },
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.029"),
              icon: "download",
              handler: () => void downloadInvoice(),
            },
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.030"),
              icon: "ban",
              handler: () => updateVoidInvoicePopup({ isOpen: true }),
              disabled: financialStatus !== "open",
            },
            ...(invoice?.parent_id
              ? [
                  {
                    type: "handler" as const,
                    label: t("View original invoice"),
                    icon: "arrow-up-right-from-square" as IconName,
                    handler: navigateToParentInvoice,
                  },
                ]
              : []),
            {
              type: "handler",
              label: t("secure.facility.order.invoices.invoice.031"),
              icon: "copy",
              handler: () => updateDuplicateInvoicePopup({ isOpen: true }),
            },
            {
              type: "handler",
              label: t("Send To"),
              icon: "mail-forward",
              handler: () => updateSendInvoiceSheet({ isOpen: true, emails: [invoice?.customer?.email] }),
            },
            ...(giftCardState?.giftCards?.length !== 0 ?
              [{
                type: "handler" as 'handler',
                label: t("Gift Card Details"),
                icon: "gift-card" as IconName,
                handler: () => updateGiftCardSheet({ isOpen: true }),
              }] : []),
          ],
        },
      }}
    >
      <Page.Section twoThirds>
        {/* Bill To */}
        <Card
          title={t("Bill to")}
          titleActions={
            financialStatus === "open" || isVoided
              ? []
              : [
                  {
                    content: t("Edit"),
                    action: () => history.push(`/admin/invoices/${id}/edit`),
                  },
                ]
          }
        >
          <Divider />
          <Card.Section>
            {invoice?.customer ? (
              <div className="invoice-bill-to">
                <div className="invoice-bill-to__column">
                  <p className="text-md text-semibold">{invoice?.customer?.full_name}</p>
                  <p className="text-md text-gray-500">{invoice?.customer?.email}</p>
                  <p className="text-md text-gray-500">{invoice?.customer?.phone}</p>
                </div>
                <div className="invoice-bill-to__column">
                  <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.048")}</p>
                  {invoice?.billing_address ? (
                    <>
                      <p className="text-md text-gray-500">{invoice?.billing_address?.address_line_1}</p>
                      {invoice?.billing_address?.address_line_2 && (
                        <p className="text-md text-gray-500">{invoice.billing_address.address_line_2}</p>
                      )}
                      <p className="text-md text-gray-500">{`${invoice?.billing_address?.city}, ${invoice?.billing_address?.province_code}`}</p>
                      <p className="text-md text-gray-500">{invoice?.billing_address?.postal}</p>
                    </>
                  ) : (
                    <p className="text-md text-gray-500">{t("secure.facility.order.invoices.invoice.049")}</p>
                  )}
                </div>
                <div className="invoice-bill-to__column">
                  <p className="text-md text-semibold">{t("secure.facility.order.invoices.invoice.050")}</p>
                  {invoice?.shipping_address ? (
                    <>
                      <p className="text-md text-gray-500">{invoice?.shipping_address?.address_line_1}</p>
                      {invoice?.shipping_address?.address_line_2 && (
                        <p className="text-md text-gray-500">{invoice.shipping_address.address_line_2}</p>
                      )}
                      <p className="text-md text-gray-500">{`${invoice?.shipping_address?.city}, ${invoice?.shipping_address?.province_code}`}</p>
                      <p className="text-md text-gray-500">{invoice?.shipping_address?.postal}</p>
                    </>
                  ) : (
                    <p className="text-md text-gray-500">{t("secure.facility.order.invoices.invoice.049")}</p>
                  )}
                </div>
              </div>
            ) : (
              <p>{t("secure.facility.order.invoices.invoice.051")}</p>
            )}
          </Card.Section>
        </Card>

        <div className="invoice-items-view">
          <p className="invoice-items-view__heading">{t("secure.facility.order.invoices.invoice.032")}</p>
          <div className="invoice-items-view__body">
            {invoiceLineItems?.map((item: any, index: number) => (
              <React.Fragment key={item.id ?? index}>
                <div className="invoice-items-view__row">
                  <div className="invoice-items-view__info">
                    <p className="invoice-items-view__title">{item.product_title}</p>
                    {item.product_title !== item.variant_title && item.variant_title ? (
                      <p className="invoice-items-view__subtitle">{item.variant_title}</p>
                    ) : null}
                  </div>
                  <p className="invoice-items-view__meta">
                    {item.quantity} x {formatCurrency(Number(item.price))}
                  </p>
                  <p className="invoice-items-view__total">{formatCurrency(item.price * item.quantity)}</p>
                </div>
                {item.modifiers?.map((modifier: any, i: number) => (
                  <div key={i} className="invoice-items-view__row invoice-items-view__row--modifier">
                    <div className="invoice-items-view__info">
                      <p className="invoice-items-view__subtitle">{modifier.product_title}</p>
                    </div>
                    <p className="invoice-items-view__meta">
                      {modifier.quantity} x {formatCurrency(Number(modifier.price))}
                    </p>
                    <p className="invoice-items-view__total">
                      {formatCurrency(modifier.price * modifier.quantity)}
                    </p>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Page.Section>

      <Page.Section oneThird>
        {/* Payment Summary */}
        <Card title={t("Payment summary")}>
          <Divider />
          <Card.Section>
            <div className="invoice-payment-summary">
              <div className="invoice-payment-summary__row">
                <span>{t("secure.facility.order.invoices.invoice.033")}</span>
                <span className="invoice-payment-summary__dots" />
                <span className="text-semibold">{formatCurrency(invoice?.subtotal_price)}</span>
              </div>
              {invoice?.tax_lines?.map((tax_line: Record<string, any>, index: number) => (
                <div key={index} className="invoice-payment-summary__row">
                  <span>
                    {tax_line.title}{" "}
                    <span className="text-gray-500">{displayPercent(Number(tax_line.rate), 3)}</span>
                  </span>
                  <span className="invoice-payment-summary__dots" />
                  <span>{formatCurrency(Number(tax_line.price))}</span>
                </div>
              ))}
              <div className="invoice-payment-summary__row">
                <span className="text-semibold">{t("secure.facility.order.invoices.invoice.034")}</span>
                <span className="invoice-payment-summary__dots" />
                <span className="text-semibold">{formatCurrency(invoice?.total_price)}</span>
              </div>

              {/* Deposits in Payment Summary */}
              {invoice?.transactions?.some(txn => txn.kind === "deposit") && (
                <>
                  <p className="invoice-payment-summary__section-label">{t("Deposits")}</p>
                  <Divider />
                  {invoice?.transactions
                    ?.filter(transaction => transaction.kind === "deposit")
                    ?.map((transaction: IInvoiceTransaction, index: number) => (
                      <div key={`dep-${index}`} className="invoice-payment-summary__row">
                        <span>
                          {valueToString(transaction.payment_type)}
                          {transaction.payment_last_4 ? (
                            <span className="text-gray-500"> •••• {transaction.payment_last_4}</span>
                          ) : null}
                        </span>
                        <span className="invoice-payment-summary__dots" />
                        <span>{formatCurrency(depositAmountToShow(transaction, invoice?.transactions ?? []))}</span>
                      </div>
                    ))}
                </>
              )}

              {/* Payments in Payment Summary */}
              {invoice?.transactions?.some(
                txn => txn.kind !== "deposit" && txn.kind !== "deposit_authorization" && txn.kind !== "refund",
              ) && (
                <>
                  <p className="invoice-payment-summary__section-label">{t("Payments")}</p>
                  <Divider />
                  {invoice?.transactions
                    ?.filter(
                      transaction =>
                        transaction.kind !== "deposit" &&
                        transaction.kind !== "deposit_authorization" &&
                        transaction.kind !== "refund",
                    )
                    ?.map((transaction: IInvoiceTransaction, index: number) => {
                      const isRefunded = transaction.refunded || locallyRefundedIds.has(transaction.id);
                      return (
                        <React.Fragment key={`pay-${index}`}>
                          <div className="invoice-payment-summary__row">
                            <span>
                              {valueToString(transaction.payment_type)}
                              {transaction.payment_last_4 ? (
                                <span className="text-gray-500"> •••• {transaction.payment_last_4}</span>
                              ) : null}
                            </span>
                            <span className="invoice-payment-summary__dots" />
                            <span>
                              {formatCurrency(
                                transaction.kind === "invoice_debit_adjustment"
                                  ? transaction.amount * -1
                                  : transaction.amount,
                              )}
                            </span>
                          </div>
                          {isRefunded && (
                            <div className="invoice-payment-summary__refund-row">
                              <FontAwesomeIcon icon={faTurnDownRight} className="invoice-payment-summary__refund-icon" />
                              <span className="invoice-payment-summary__refund-label text-semibold">
                                {t("Refunded")}
                              </span>
                              <span className="invoice-payment-summary__dots" />
                              <span>{formatCurrency(transaction.amount * -1)}</span>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                </>
              )}

              <Divider />
              <div className="invoice-payment-summary__row invoice-payment-summary__row--total">
                <span className="text-semibold" style={{ fontSize: "16px" }}>{t("Balance due")}</span>
                <span className="invoice-payment-summary__dots" />
                <span className={classNames({ "invoice-balance-due": balance > 0 }, "text-semibold")} style={{ fontSize: "16px", lineHeight: "24px" }}>
                  {formatCurrency(balance)}
                </span>
              </div>
            </div>
          </Card.Section>
          {invoice?.financial_status === "paid" && invoice?.balance < 0 && (
            <Card.Section>
              <Button type="primary" onClick={() => updateAdjustBalancePopup({ isOpen: true })}>
                {t("Adjust Balance")}
              </Button>
            </Card.Section>
          )}
        </Card>

        {/* Payment Activity */}
        <Card title={t("Payment activity")}>
          <Divider />
          <Card.Section>
            <div className="invoice-payment-activity">
              {hasNoPaymentActivity ? (
                <div className="invoice-payment-activity__empty">
                  <div className="invoice-payment-activity__empty-icon">
                    <FontAwesomeIcon icon={faMoneyBill} />
                  </div>
                  <p className="invoice-payment-activity__empty-title">{t("No payment activity yet")}</p>
                  <p className="invoice-payment-activity__empty-description">
                    {t("Add a deposit or make a payment to record money received for this invoice.")}
                  </p>
                  <div className="invoice-payment-activity__empty-actions">
                    <Button type="secondary" size="medium" onClick={() => setDepositModalVisible(true)}>
                      {t("Add deposit")}
                    </Button>
                    <Button type="primary" size="medium" onClick={() => setPaymentModalVisible(true)}>
                      {t("Make payment")}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {hasEvent &&
                    (visibleDeposits.length > 0 ? (
                      <>
                        {visibleDeposits.map((deposit, index) => {
                          const isApplied = deposit?.invoice_id !== null;
                          return (
                            <div
                              key={deposit?.id}
                              className={classNames("invoice-payment-activity__item", {
                                "invoice-payment-activity__item--applied": isApplied,
                              })}
                            >
                              <div
                                className={classNames("invoice-payment-activity__icon", {
                                  "invoice-payment-activity__icon--success": isApplied,
                                  "invoice-payment-activity__icon--default": !isApplied,
                                })}
                              >
                                <FontAwesomeIcon icon={isApplied ? faCheckCircle : faCircleInfo} size="sm" />
                              </div>
                              <div className="invoice-payment-activity__content">
                                <span className="invoice-payment-activity__title">
                                  {t("Deposit")} {index + 1}
                                </span>
                                <span className="invoice-payment-activity__subtitle">
                                  {formatCurrency(depositAmountToShow(deposit, eventDeposits))} • {valueToString(deposit?.payment_type)}
                                </span>
                              </div>
                              <div className="invoice-payment-activity__action">
                                {isApplied ? (
                                  <Button
                                    type="text"
                                    size="small"
                                    onClick={() =>
                                      updateRemoveDepositPopup({ isOpen: true, depositId: deposit?.id })
                                    }
                                  >
                                    <span
                                      className="invoice-text-error"
                                      style={{ fontWeight: 600, fontSize: "12px" }}
                                    >
                                      {t("Remove")}
                                    </span>
                                  </Button>
                                ) : (
                                  <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() =>
                                      updateApplyDepositPopup({ isOpen: true, depositId: deposit?.id })
                                    }
                                  >
                                    {t("Apply")}
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          className="invoice-payment-activity__add-deposit"
                          onClick={() => setDepositModalVisible(true)}
                        >
                          <FontAwesomeIcon icon={faPlus} size="sm" />
                          {t("Add deposit")}
                        </button>
                      </>
                    ) : (
                      <div className="invoice-payment-activity__empty">
                        <div className="invoice-payment-activity__empty-icon">
                          <FontAwesomeIcon icon={faMoneyBill} />
                        </div>
                        <p className="invoice-payment-activity__empty-title">{t("No deposits yet")}</p>
                        <p className="invoice-payment-activity__empty-description">
                          {t("There aren't any deposits attached to this invoice yet")}
                        </p>
                        <Button
                          type="primary"
                          size="medium"
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          onClick={() => setDepositModalVisible(true)}
                        >
                          {t("Add deposit")}
                        </Button>
                      </div>
                    ))}

                  {hasEvent && <Divider />}

                  {visiblePayments.length > 0 ? (
                    visiblePayments.map((transaction: IInvoiceTransaction, index: number) => {
                      const isRefunded = transaction.refunded || locallyRefundedIds.has(transaction.id);
                      return (
                        <div
                          key={`payment-${transaction.id ?? index}`}
                          className={classNames("invoice-payment-activity__item", {
                            "invoice-payment-activity__item--refunded": isRefunded,
                          })}
                        >
                          <div className="invoice-payment-activity__icon invoice-payment-activity__icon--default">
                            <FontAwesomeIcon icon={faCircleInfo} size="sm" />
                          </div>
                          <div className="invoice-payment-activity__content">
                            <span className="invoice-payment-activity__title">
                              {t("Payment")} {index + 1}
                            </span>
                            <span className="invoice-payment-activity__subtitle">
                              {formatCurrency(transaction?.amount)} • {valueToString(transaction?.payment_type)}
                            </span>
                          </div>
                          <div className="invoice-payment-activity__action">
                            {isRefunded ? (
                              <Badge type="error" size="small">
                                {t("Refunded")}
                              </Badge>
                            ) : (
                              !isVoided && (
                                <Button type="secondary" size="small" onClick={() => void postRefund(transaction)}>
                                  {t("Refund")}
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="invoice-payment-activity__empty">
                      <div className="invoice-payment-activity__empty-icon">
                        <FontAwesomeIcon icon={faMoneyBill} />
                      </div>
                      <p className="invoice-payment-activity__empty-title">{t("No payments made yet")}</p>
                      <p className="invoice-payment-activity__empty-description">
                        {t("There aren't any payments made to this invoice yet")}
                      </p>
                      <Button
                        type="primary"
                        size="medium"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setPaymentModalVisible(true)}
                      >
                        {t("Make payment")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card.Section>
        </Card>

        {/* Invoice Details */}
        <Card title={t("Invoice Details")}>
          <Card.Section>
            <div className="invoice-details-grid">
              <div className="invoice-details-grid__row">
                <span className="invoice-details-grid__label">{t("Invoice Date")}</span>
                <span className="invoice-details-grid__value">{invoice?.date || "-"}</span>
              </div>
              <div className="invoice-details-grid__row">
                <span className="invoice-details-grid__label">{t("Due Date")}</span>
                <span className="invoice-details-grid__value invoice-details-grid__value--stack">
                  <span>{invoice?.due_date || "-"}</span>
                  {paymentTermsLabel ? (
                    <span className="invoice-details-grid__value--secondary">{paymentTermsLabel}</span>
                  ) : null}
                </span>
              </div>
              <div className="invoice-details-grid__row">
                <span className="invoice-details-grid__label">{t("Register")}</span>
                <span
                  className={classNames("invoice-details-grid__value", {
                    "invoice-details-grid__value--empty": !registerTitle,
                  })}
                >
                  {registerTitle || "-"}
                </span>
              </div>
              <div className="invoice-details-grid__row">
                <span className="invoice-details-grid__label">{t("PO Number")}</span>
                <span
                  className={classNames("invoice-details-grid__value", {
                    "invoice-details-grid__value--empty": !invoice?.po_number,
                  })}
                >
                  {invoice?.po_number || "-"}
                </span>
              </div>
              <div className="invoice-details-grid__row">
                <span className="invoice-details-grid__label">{t("Note")}</span>
                <span
                  className={classNames("invoice-details-grid__value invoice-details-grid__value--note", {
                    "invoice-details-grid__value--empty": !invoice?.notes,
                  })}
                >
                  {invoice?.notes ? <Markdown markdownText={invoice.notes} /> : "-"}
                </span>
              </div>
            </div>
          </Card.Section>
        </Card>

        {/* Event */}
        {(invoice?.tournament || invoice?.hospitality_event) && (
          <Card title={t("Event")}>
            <Divider />
            <Card.Section>
              {invoice?.tournament ? (
                <div>
                  <p className="text-md text-semibold">{invoice?.tournament?.name}</p>
                  <p className="text-sm text-gray-500">
                    {`${invoice?.tournament?.total_participant_count}/${Number(
                      invoice?.tournament?.player_limit,
                    )} ${t("secure.facility.order.invoices.invoice.053")}`}
                    {" • "}
                    {invoice?.tournament?.date}
                  </p>
                </div>
              ) : invoice?.hospitality_event ? (
                <div>
                  <p className="text-md text-semibold">{invoice?.hospitality_event?.name}</p>
                  <p className="text-sm text-gray-500">{invoice?.hospitality_event?.date}</p>
                </div>
              ) : null}
            </Card.Section>
          </Card>
        )}
      </Page.Section>

      {invoice && (paymentModalVisible || depositModalVisible) && (
        <InvoicePaymentModal
          key={depositModalVisible ? "deposit" : "payment"}
          paymentModalVisible={paymentModalVisible || depositModalVisible}
          invoice={invoice}
          paymentMethods={null} // TODO: Load current customer payment methods
          onOk={() => void loadInvoice()}
          closePaymentModal={() => {
            setPaymentModalVisible(false);
            setDepositModalVisible(false);
          }}
          customer={invoice?.customer}
          depositOnly={depositModalVisible}
          events={events}
          eventSearchValue={eventQuery}
          onEventSearch={(query: string) => setEventQuery(query)}
        />
      )}

      {invoice && sendInvoiceSheet && (
        <EmailInvoiceModal
          state={sendInvoiceSheet}
          customerEmail={invoice?.customer?.email}
          update={updateSendInvoiceSheet}
          close={closeSendInvoiceSheet}
          onOk={() => void sendInvoice()}
        />
      )}

      <Portal isMounted={voidInvoicePopup.isOpen}>
        <Popup
          open={voidInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.063")}
          description={t("secure.facility.order.invoices.invoice.064")}
          onOk={voidInvoice}
          okText={t("secure.facility.order.invoices.invoice.065")}
          onCancel={closeVoidInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={duplicateInvoicePopup.isOpen}>
        <Popup
          open={duplicateInvoicePopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.066")}
          description={t("secure.facility.order.invoices.invoice.067")}
          onOk={duplicateInvoice}
          okText={t("secure.facility.order.invoices.invoice.068")}
          onCancel={closeDuplicateInvoicePopup}
          type="warning"
        />
      </Portal>

      <Portal isMounted={addAdjustmentsPopup.isOpen}>
        <Popup
          open={addAdjustmentsPopup.isOpen}
          className="invoice-edit-confirm-popup"
          title={t("Edit Invoice {{name}}?", { name: invoice?.name })}
          description={
            <>
              <p>
                {t(
                  "This invoice will be voided, and a copy will be created as a new draft. Existing items stay locked, but you can add new items or edit the invoice.",
                )}
              </p>
              <p>{t("This action cannot be undone.")}</p>
            </>
          }
          onOk={editInvoice}
          okText={t("Void & create draft")}
          onCancel={closeAddAdjustmentsPopup}
          type="warning"
          aligned="left"
        />
      </Portal>

      <Portal isMounted={applyDepositPopup.isOpen}>
        <Popup
          open={applyDepositPopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.072")}
          description={t("secure.facility.order.invoices.invoice.073")}
          type="warning"
          onCancel={() => closeApplyDepositPopup()}
          onOk={() => applyDeposit(Number(applyDepositPopup.depositId))}
        />
      </Portal>

      <Portal isMounted={removeDepositPopup.isOpen}>
        <Popup
          open={removeDepositPopup.isOpen}
          title={t("secure.facility.order.invoices.invoice.074")}
          description={t("secure.facility.order.invoices.invoice.075")}
          type="warning"
          onCancel={() => closeRemoveDepositPopup()}
          onOk={() => removeDeposit(Number(removeDepositPopup.depositId))}
        />
      </Portal>

      <Portal isMounted={adjustBalancePopup.isOpen}>
        <Popup
          open={adjustBalancePopup.isOpen}
          title={t("Adjust Balance?")}
          description={t("Are you sure you want to adjust the balance of this this invoice?")}
          type="warning"
          onCancel={() => closeAdjustBalancePopup()}
          onOk={() => createBalanceAdjustment()}
        >
          <div className="mt-2">
            <Input
              label={t("New Balance")}
              value={adjustBalancePopup.newBalance}
              onChange={e => updateAdjustBalancePopup({ newBalance: Number(e.target.value) })}
              type={"number"}
            />
          </div>
        </Popup>
      </Portal>

      <Portal isMounted={giftCardSheet.isOpen}>
        <Sheet
          title={t("Invoice Gift Cards")}
          open={giftCardSheet.isOpen}
          closable
          size="small"
          onCancel={() => closeGiftCardSheet()}
          cancelText={t("Cancel")}
        >
          <DataTable
            columns={[
              { label: t("Gift Card Code") },
              { label: t("Balance") },
              { label: t("Pin") },
              { label: "" },
            ]}
          >
            {giftCardState?.giftCards?.map(giftCard => (
              <tr
                key={giftCard.id}
                id={String(giftCard.id)}
                className="clickable"
                onClick={() => navigateToGiftCard(giftCard.code)}
              >
                <td>{giftCard.code}</td>
                <td>${giftCard.balance}</td>
                <td>{giftCard.pin}</td>
                <td onClick={e => void handleGiftCardPrint(giftCard.id, e)}>{t("Print")}</td>
              </tr>
            ))}
          </DataTable>
        </Sheet>
      </Portal>

      {/* Modal for refunding Interac transactions  */}
      <StripeTerminalRefundSheet />
    </Page>
  );
}

function generateStatusBadge(status: string) {
  switch (status) {
    case "draft":
      return (
        <Badge type="primary" outline className="invoice-status-badge--draft" iconLeft={<FontAwesomeIcon icon={faPen} />}>
          Draft
        </Badge>
      );
    case "open":
      return (
        <Badge type="warning" outline iconLeft={<FontAwesomeIcon icon={faClock} />}>
          Open
        </Badge>
      );
    case "paid":
      return <Badge type="success">{valueToString(status, "capitalize")}</Badge>;
    case "overpaid":
      return (
        <Badge
          type="warning"
          outline
          className="invoice-status-badge--overpaid"
          iconLeft={<FontAwesomeIcon icon={faCoins} />}
        >
          Overpaid
        </Badge>
      );
    case "partially_paid":
      return <Badge type="warning">{valueToString(status, "capitalize")}</Badge>;
    case "voided":
      return (
        <Badge type="gray" iconLeft={<FontAwesomeIcon icon={faBan} />}>
          Void
        </Badge>
      );
    default:
      return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
  }
}
